import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { canSaveStory } from '../../utils/authChecks';
import SignupPrompt from '../Account/SignupPrompt';
import Notification from '../Account/Notification';

interface BookPage {
  id: number;
  content: string;
  title: string;
}

interface ChapterMasterProps {
  theme?: {
    primary: string;
    secondary: string;
    accent: string;
    success: string;
    pink?: string;
    blue?: string;
  };
  book?: BookPage[];
  currentPageIndex?: number;
  onUpdateBook?: (newBook: BookPage[]) => void;
  onNavigateToPage?: (index: number) => void;
  onClose?: () => void;
  onSave?: () => void;
}

const ChapterMaster: React.FC<ChapterMasterProps> = ({ 
  theme = {
    primary: '#FF6B35',
    secondary: '#9B59B6',
    accent: '#F39C12',
    success: '#2ECC71',
    pink: '#E91E63',
    blue: '#3498DB'
  }, 
  book = [
    { id: 1, title: 'Chapter 1: The Beginning', content: 'Once upon a time...' },
    { id: 2, title: 'Chapter 2: The Quest Begins', content: 'The mysterious stranger appeared...' },
    { id: 3, title: 'Chapter 3: New Friends', content: '' },
    { id: 4, title: 'Chapter 4: The Challenge', content: '' }
  ],
  currentPageIndex = 0, 
  onUpdateBook = (newBook) => console.log('Book updated:', newBook), 
  onNavigateToPage = (index) => console.log('Navigate to page:', index),
  onClose,
  onSave
}) => {
  const { currentUser } = useAuth();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [showSignupPrompt, setShowSignupPrompt] = useState(false);
  const [activeView, setActiveView] = useState<'chapters' | 'templates'>('chapters');

  const [notification, setNotification] = useState<{
    show: boolean;
    type: 'save' | 'error';
    message?: string;
  }>({ show: false, type: 'save' });
  

  // Templates
  const templates = [
    {
      name: 'Hero\'s Journey',
      icon: '⚔️',
      description: 'A classic adventure structure',
      chapters: ['Ordinary World', 'Call to Adventure', 'Meeting the Mentor', 'Tests & Trials', 'The Ordeal', 'Return Home']
    },
    {
      name: 'Mystery Quest',
      icon: '🔍',
      description: 'Solve a thrilling mystery',
      chapters: ['The Mystery', 'First Clues', 'Red Herrings', 'The Big Reveal', 'Solving the Case']
    },
    {
      name: 'Three Act Story',
      icon: '🎬',
      description: 'Classic beginning, middle, and end',
      chapters: ['Setup', 'Confrontation', 'Rising Stakes', 'Climax', 'Resolution']
    },
    {
      name: 'Quest Adventure',
      icon: '🗺️',
      description: 'An epic journey story',
      chapters: ['The Quest Begins', 'Finding Allies', 'Facing Dangers', 'The Final Challenge', 'Victory & Return']
    }
  ];

  const applyTemplate = (template: typeof templates[0]) => {
    setConfirmModal({ show: true, template });
  };
  
  const handleConfirmApply = () => {
    if (!confirmModal.template) return;
    
    const template = confirmModal.template;
    const newBook: BookPage[] = template.chapters.map((chapterName, index) => ({
      id: Date.now() + index,
      title: `Chapter ${index + 1}: ${chapterName}`,
      content: ''
    }));
    
    onUpdateBook(newBook);
    // REMOVE: onNavigateToPage(0);
    setActiveView('chapters');  // Stay on chapters view
    setConfirmModal({ show: false, template: null });
    
    setNotification({
      show: true,
      type: 'save',
      message: `✨ Template applied! ${template.chapters.length} chapters created.`
    });
  };

  const [confirmModal, setConfirmModal] = useState<{
    show: boolean;
    template: typeof templates[0] | null;
  }>({ show: false, template: null });

  const getChapterIcon = (index: number) => {
    if (index === 0) return '🌅';
    if (index === book.length - 1) return '🌟';
    const icons = ['⚡', '💭', '🎭', '🔍', '💥'];
    return icons[index % icons.length];
  };

  const getChapterAnalysis = (page: BookPage) => {
    const wordCount = page.content.trim() === '' ? 0 : page.content.trim().split(/\s+/).length;
    
    let status = 'Empty';
    let color = '#ccc';
    
    if (wordCount === 0) {
      status = 'Ready to write!';
      color = theme.accent;
    } else if (wordCount < 50) {
      status = 'Getting started';
      color = theme.secondary;
    } else if (wordCount < 200) {
      status = 'Great progress!';
      color = theme.primary;
    } else {
      status = 'Amazing work!';
      color = theme.success;
    }

    return { wordCount, status, color };
  };

  const addChapterAfter = (index: number) => {
    const newChapter: BookPage = {
      id: Date.now(),
      title: `Chapter ${book.length + 1}`,
      content: ''
    };
    const newBook = [...book];
    newBook.splice(index + 1, 0, newChapter);
    onUpdateBook(newBook);
  };

  const deleteChapter = (index: number) => {
    if (book.length > 1) {
      const newBook = book.filter((_, i) => i !== index);
      onUpdateBook(newBook);
      if (currentPageIndex >= index && currentPageIndex > 0) {
        onNavigateToPage(Math.max(0, currentPageIndex - 1));
      }
    }
  };

  const moveChapter = (fromIndex: number, toIndex: number) => {
    const newBook = [...book];
    const [movedChapter] = newBook.splice(fromIndex, 1);
    newBook.splice(toIndex, 0, movedChapter);
    onUpdateBook(newBook);
    
    if (currentPageIndex === fromIndex) {
      onNavigateToPage(toIndex);
    } else if (currentPageIndex > fromIndex && currentPageIndex <= toIndex) {
      onNavigateToPage(currentPageIndex - 1);
    } else if (currentPageIndex < fromIndex && currentPageIndex >= toIndex) {
      onNavigateToPage(currentPageIndex + 1);
    }
  };

  const totalWords = book.reduce((sum, page) => sum + (page.content.trim() === '' ? 0 : page.content.trim().split(/\s+/).length), 0);
  const completedChapters = book.filter(page => page.content.trim().length > 50).length;

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      window.history.back();
    }
  };

  return (
    <>
      <div style={{
  background: '#FAFAF9',
  minHeight: '100vh',
  paddingBottom: '40px'
}}>
  {/* Floating Header Bar */}
  <div style={{
    padding: '20px 40px',
    background: '#FAFAF9'
  }}>
    <div style={{
      maxWidth: '1450px',
      margin: '0 auto'
    }}>
      <div style={{
        height: 52,
        borderRadius: 14,
        background: 'rgba(255,255,255,0.95)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        boxShadow: '0 6px 18px rgba(0,0,0,0.08)'
      }}>
        <div style={{
          fontSize: '18px',
          fontWeight: '700',
          color: '#1a3a52'
        }}>
          📚 Chapter Master
        </div>
        
        <button
          onClick={handleClose}
          style={{
            background: '#6C5CE7',
            color: 'white',
            border: 'none',
            borderRadius: '10px',
            padding: '10px 20px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(108, 92, 231, 0.3)'
          }}
        >
          Return to Writing →
        </button>
      </div>
    </div>
  </div>

  <div style={{
    maxWidth: '1450px',
    margin: '0 auto',
    padding: '0 40px'
  }}>
  
          {/* Title */}
<div style={{
  textAlign: 'center',
  marginBottom: '16px'
}}>
  <h1 style={{
    fontSize: '32px',
    fontWeight: '800',
    margin: 0,
    color: '#1a3a52'
  }}>
    📚 Chapter Master
  </h1>
</div>

          {/* Subheading */}
          <div style={{
            textAlign: 'center',
            marginBottom: '32px'
          }}>
            <p style={{ 
              fontSize: '16px', 
              color: '#666', 
              margin: 0,
              fontWeight: '500'
            }}>
              Organize and manage all your chapters
            </p>
          </div>
          {/* Tab Buttons */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '24px' }}>
            <button
              onClick={() => setActiveView('chapters')}
              style={{
                flex: 1,
                padding: '12px',
                background: activeView === 'chapters' ? '#6C5CE7' : 'white',
                color: activeView === 'chapters' ? 'white' : '#666',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              📖 My Chapters
            </button>
            <button
              onClick={() => setActiveView('templates')}
              style={{
                flex: 1,
                padding: '12px',
                background: activeView === 'templates' ? '#6C5CE7' : 'white',
                color: activeView === 'templates' ? 'white' : '#666',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                fontSize: '15px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
            >
              ✨ Templates
            </button>
          </div>

          {/* CHAPTERS VIEW */}
          {activeView === 'chapters' && (
            <>
              {/* Story Overview */}
              <div style={{
                background: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px'
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: '16px'
                }}>
                  <h3 style={{
                    margin: 0,
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1a3a52'
                  }}>
                    Your Story Overview
                  </h3>
                  <div style={{
                    background: '#6C5CE7',
                    color: 'white',
                    padding: '6px 12px',
                    borderRadius: '12px',
                    fontSize: '13px',
                    fontWeight: '600'
                  }}>
                    {book.length} chapters
                  </div>
                </div>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
                  gap: '12px'
                }}>
                  <div style={{
                    background: '#f8f9fa',
                    padding: '16px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#6C5CE7', marginBottom: '4px' }}>
                      {totalWords.toLocaleString()}
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>Total Words</div>
                  </div>
                  
                  <div style={{
                    background: '#f8f9fa',
                    padding: '16px',
                    borderRadius: '8px',
                    textAlign: 'center',
                    border: '1px solid #e0e0e0'
                  }}>
                    <div style={{ fontSize: '24px', fontWeight: '700', color: '#6C5CE7', marginBottom: '4px' }}>
                      {completedChapters}/{book.length}
                    </div>
                    <div style={{ fontSize: '13px', color: '#666' }}>Completed</div>
                  </div>
                </div>
              </div>

              {/* Chapter List */}
              <div style={{
                background: 'white',
                border: '1px solid #e0e0e0',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '24px'
              }}>
                <h3 style={{
                  margin: '0 0 16px 0',
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1a3a52'
                }}>
                  Manage Your Chapters
                </h3>
                
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '12px',
                  maxHeight: '400px',
                  overflowY: 'auto',
                  padding: '5px'
                }}>
                  {book.map((page, index) => {
  const analysis = getChapterAnalysis(page);
  const isActive = currentPageIndex === index;
  
  return (
    <div 
      key={page.id}
      draggable
      onDragStart={() => setDraggedIndex(index)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        if (draggedIndex !== null && draggedIndex !== index) {
          moveChapter(draggedIndex, index);
        }
        setDraggedIndex(null);
      }}
      style={{ 
        background: 'white',
        border: `2px solid ${isActive ? '#6C5CE7' : '#e0e0e0'}`,
        borderRadius: '12px',
        padding: '20px',
        cursor: 'grab',
        transition: 'all 0.2s ease',
        opacity: draggedIndex === index ? 0.5 : 1
      }}
      onMouseEnter={(e) => {
        if (!isActive) e.currentTarget.style.borderColor = '#ccc';
      }}
      onMouseLeave={(e) => {
        if (!isActive) e.currentTarget.style.borderColor = '#e0e0e0';
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
          <span style={{ fontSize: '24px' }}>{getChapterIcon(index)}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '13px', color: '#999', marginBottom: '4px' }}>
              Chapter {index + 1}
            </div>
            <input
              type="text"
              value={page.title}
              onChange={(e) => {
                const newBook = [...book];
                newBook[index] = { ...newBook[index], title: e.target.value };
                onUpdateBook(newBook);
              }}
              onClick={(e) => e.stopPropagation()}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '16px',
                fontWeight: '600',
                color: '#1a3a52',
                width: '100%',
                outline: 'none',
                cursor: 'text',
                padding: '4px 0'
              }}
              placeholder={`Chapter ${index + 1}`}
            />
            <div style={{
              fontSize: '12px',
              color: '#666',
              marginTop: '4px'
            }}>
              {analysis.wordCount} words • {analysis.status}
            </div>
          </div>
        </div>
        
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => addChapterAfter(index)}
            style={{
              background: 'transparent',
              color: '#6C5CE7',
              border: 'none',
              borderRadius: '6px',
              padding: '8px',
              cursor: 'pointer',
              fontSize: '20px'
            }}
            title="Add chapter after"
          >
            ➕
          </button>
          {book.length > 1 && (
            <button
              onClick={() => deleteChapter(index)}
              style={{
                background: 'transparent',
                color: '#ff4757',
                border: 'none',
                borderRadius: '6px',
                padding: '8px',
                cursor: 'pointer',
                fontSize: '20px'
              }}
              title="Delete chapter"
            >
              🗑️
            </button>
          )}
        </div>
      </div>
    </div>
  );
})}
                </div>
              </div>
            </>
          )}

          {/* TEMPLATES VIEW */}
          {activeView === 'templates' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{
                background: '#F3F0FF',
                border: '2px solid #6C5CE7',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '8px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                  <span style={{ fontSize: '24px' }}>✨</span>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#1a3a52' }}>
                    Story Structure Templates
                  </h3>
                </div>
                <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                  Choose a template to quickly set up your story structure. Your current chapters will be replaced.
                </p>
              </div>

              {templates.map((template, idx) => (
                <div
                  key={idx}
                  style={{
                    background: 'white',
                    border: '1px solid #e0e0e0',
                    borderRadius: '12px',
                    padding: '20px'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <span style={{ fontSize: '24px' }}>{template.icon}</span>
                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600', color: '#1a3a52' }}>
                          {template.name}
                        </h3>
                      </div>
                      <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                        {template.description}
                      </p>
                    </div>
                    <button
                      onClick={() => applyTemplate(template)}
                      style={{
                        padding: '8px 16px',
                        background: '#6C5CE7',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#5B4BC4';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = '#6C5CE7';
                      }}
                    >
                      Use Template
                    </button>
                  </div>
                  <div style={{ 
                    fontSize: '13px', 
                    color: '#888',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px'
                  }}>
                    {template.chapters.map((ch, i) => (
                      <span 
                        key={i}
                        style={{
                          background: '#f8f9fa',
                          padding: '4px 10px',
                          borderRadius: '6px',
                          border: '1px solid #e0e0e0'
                        }}
                      >
                        {i + 1}. {ch}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Modal */}
      {confirmModal.show && confirmModal.template && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            margin: '20px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
          }}>
            <h3 style={{
              margin: '0 0 16px 0',
              fontSize: '20px',
              fontWeight: '700',
              color: '#1a3a52'
            }}>
              Replace chapters with "{confirmModal.template.name}"?
            </h3>
            <p style={{
              margin: '0 0 24px 0',
              fontSize: '15px',
              color: '#666',
              lineHeight: '1.6'
            }}>
              This will create <strong>{confirmModal.template.chapters.length} new chapters</strong>. Your current work will be lost.
              <br /><br />
              Are you sure?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setConfirmModal({ show: false, template: null })}
                style={{
                  padding: '12px 24px',
                  background: '#f5f5f5',
                  color: '#666',
                  border: '1px solid #e0e0e0',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmApply}
                style={{
                  padding: '12px 24px',
                  background: '#6C5CE7',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                Yes, Replace
              </button>
            </div>
          </div>
        </div>
      )}

      <SignupPrompt
        isOpen={showSignupPrompt}
        onClose={() => setShowSignupPrompt(false)}
        reason="save"
      />
      <Notification
        isOpen={notification.show}
        onClose={() => setNotification({ ...notification, show: false })}
        type={notification.type}
        customMessage={notification.message}
      />
    </>
  );
};

export default ChapterMaster;